import React, { type FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import dayjs, { type Dayjs } from 'dayjs'
import { Typography } from '../ui/Typography'
import { colors } from '../styles/colors'

interface CountdownTimerProps {
  targetDate: Dayjs | null
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export const CountdownTimer: FC<CountdownTimerProps> = ({ targetDate }) => {
  const { formatMessage } = useIntl()
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null)

  useEffect(() => {
    if (!targetDate) {
      setTimeLeft(null)
      return
    }

    const calculateTimeLeft = (): TimeLeft | null => {
      const now = dayjs()
      const diff = targetDate.diff(now)

      if (diff <= 0) {
        return null
      }

      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      }
    }

    setTimeLeft(calculateTimeLeft())

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(interval)
  }, [targetDate])

  if (!timeLeft) {
    return null
  }

  const parts: string[] = []

  if (timeLeft.days > 0) {
    parts.push(
      formatMessage(
        {
          id: 'countdown.days',
          defaultMessage:
            '{count, plural, one {# день} few {# дня} many {# дней} other {# дней}}',
        },
        { count: timeLeft.days },
      ),
    )
  }

  if (timeLeft.hours > 0) {
    parts.push(
      formatMessage(
        {
          id: 'countdown.hours',
          defaultMessage:
            '{count, plural, one {# час} few {# часа} many {# часов} other {# часов}}',
        },
        { count: timeLeft.hours },
      ),
    )
  }

  if (timeLeft.minutes > 0) {
    parts.push(
      formatMessage(
        {
          id: 'countdown.minutes',
          defaultMessage:
            '{count, plural, one {# минута} few {# минуты} many {# минут} other {# минут}}',
        },
        { count: timeLeft.minutes },
      ),
    )
  }

  if (timeLeft.seconds > 0 || parts.length === 0) {
    parts.push(
      formatMessage(
        {
          id: 'countdown.seconds',
          defaultMessage:
            '{count, plural, one {# секунда} few {# секунды} many {# секунд} other {# секунд}}',
        },
        { count: timeLeft.seconds },
      ),
    )
  }

  const isUrgent = timeLeft.days < 3

  return (
    <Typography
      fontSize={10}
      fontWeight={400}
      color={isUrgent ? colors.danger : undefined}
    >
      {formatMessage(
        {
          id: 'newBox.timeLeft',
          defaultMessage: 'Осталось: {time}',
        },
        {
          time: parts.join(' '),
        },
      )}
    </Typography>
  )
}
